/* istanbul ignore file */
import {
  Box,
  Button,
  Dialog,
  Flex,
  Text,
  TextArea,
  useToast_UNSTABLE as useToast,
} from '@kandji-inc/nectar-ui';
import deepcopy from 'deepcopy';
import { useContext, useEffect, useState } from 'react';
import {
  SIDEBAR_CLOSED_OFFSET,
  SIDEBAR_OPENED_OFFSET,
} from 'src/app/common/constants';
import { AccountContext } from 'src/contexts/account';
import { InterfaceContext } from 'src/contexts/interface';
import { i18n } from 'src/i18n';
import { useProvideTrialFeedback } from './useProvideFeedback';

const defaultModel = {
  comments: '',
};

const MAX_COMMENTS_LENGTH = 320;

const ProvideFeedbackDialog = ({
  pageName,
  isOpen,
  setIsOpen,
  withError = null,
}: {
  pageName: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  withError: string;
}) => {
  const { currentCompany, userEmail, userFirstName, userLastName } =
    useContext(AccountContext);

  const { sidebarOpened } = useContext(InterfaceContext);
  const { toast } = useToast();

  const tenant = currentCompany?.subdomain || '';

  const [model, setModel] = useState(deepcopy(defaultModel));

  const { mutateAsync: provideFeedback, isPending } = useProvideTrialFeedback();

  const onProvideFeedback = () =>
    provideFeedback({
      tenant,
      userEmail,
      userFirstName,
      userLastName,
      pageName,
      comments: model.comments,
    })
      .then(() => {
        toast({
          title: 'Thank you for your feedback!',
          variant: 'success',
          duration: 2500,
          style: {
            left: /* istanbul ignore next */ sidebarOpened
              ? `${SIDEBAR_OPENED_OFFSET + 12}px`
              : `${SIDEBAR_CLOSED_OFFSET + 12}px`,
            bottom: '12px',
            position: 'absolute',
          },
        });
        setIsOpen(false);
      })
      .catch(() =>
        toast({
          title: 'Failed to send feedback. Please try again.',
          variant: 'error',
          duration: 2500,
          style: {
            left: /* istanbul ignore next */ sidebarOpened
              ? `${SIDEBAR_OPENED_OFFSET + 12}px`
              : `${SIDEBAR_CLOSED_OFFSET + 12}px`,
            bottom: '12px',
            position: 'absolute',
          },
        }),
      );

  useEffect(() => {
    if (isOpen) {
      setModel(deepcopy(defaultModel));
    }
  }, [isOpen]);

  const title = i18n.t('Provide feedback');
  const content = (
    <Flex mb4 flow="column" gap="lg">
      {withError ? (
        <Text variant="danger" css={{ marginTop: '4px' }}>
          {withError}
        </Text>
      ) : (
        <Text>
          {i18n.t(
            'Your feedback is essential as we work to enhance our trial experience.',
          )}
        </Text>
      )}
      <Box css={{ height: '140px', '& textArea': { height: '140px' } }}>
        <TextArea
          placeholder="Please share any feature suggestions, improvements, or ways we could have better supported you during your trial. Thank you for helping us improve!"
          onChange={(e) =>
            setModel((prev) => ({ ...prev, comments: e.target.value }))
          }
          value={model.comments}
          hint={{
            charCounter: {
              count: model.comments.length,
              max: MAX_COMMENTS_LENGTH,
            },
          }}
          autoFocus
        />
      </Box>
    </Flex>
  );

  const footer = (
    <Flex justifyContent="end" alignItems="center" gap="sm">
      <Button
        loading={isPending}
        variant="subtle"
        onClick={() => setIsOpen(false)}
      >
        {i18n.t('Cancel')}
      </Button>
      <Button
        loading={isPending}
        disabled={model.comments.length >= MAX_COMMENTS_LENGTH}
        variant="primary"
        onClick={() => onProvideFeedback()}
      >
        {i18n.t('Send feedback')}
      </Button>
    </Flex>
  );

  return (
    <Dialog
      css={{
        width: '480px',
      }}
      isOpen={isOpen}
      closeOnEscape
      onOpenChange={setIsOpen}
      title={title}
      content={content}
      footer={footer}
    />
  );
};

export default ProvideFeedbackDialog;
